import { useEffect, useState, useCallback } from "react";
import { PieChart, Pie, Sector, ResponsiveContainer, Cell } from "recharts";
import { Link } from "react-router-dom";

import Toaster from "../../../components/common/Toaster";
import dashboardService from "../../../services/dashboardService";

const RegistrationFeeSubmission = ({ year }) => {
  const [data, setData] = useState([]);
  const [filedCount, setFiledCount] = useState(0);
  const [unfiledCount, setUnfiledCount] = useState(0);
  const [activeIndex, setActiveIndex] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = useCallback(
    async (mounted) => {
      try {
        setIsLoading(true);

        const { result } =
          await dashboardService.getRegistrationFeeSubmissionAsync(year);

        const filed = result
          .filter((i) => i.status === 2)
          .map((i) => i.total_count);

        const unfiled = result
          .filter((i) => i.status === 1)
          .map((i) => i.total_count);

        const items = [
          { name: "Unfiled", total_count: unfiled[0] },
          { name: "Filed", total_count: filed[0] },
        ];

        if (mounted) {
          setData(items);
          setFiledCount(filed.length > 0 ? filed : 0);
          setUnfiledCount(unfiled.length > 0 ? unfiled : 0);
          setIsLoading(false);

          if (unfiled.length === 0) {
            setActiveIndex(1);
          } else {
            setActiveIndex(0);
          }
        }
      } catch (ex) {
        Toaster.Error("Something went wrong.");
        setIsLoading(false);
      }
    },
    [year]
  );

  useEffect(() => {
    let mounted = true;

    fetchData(mounted);

    return () => {
      mounted = false;
    };
  }, [fetchData]);

  const renderActiveShape = (props) => {
    const RADIAN = Math.PI / 180;
    const {
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      fillOpacity,
      payload,
      percent,
    } = props;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 15) * cos;
    const my = cy + (outerRadius + 15) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 15;
    const ey = my;
    const textAnchor = cos >= 0 ? "start" : "end";

    return (
      <g>
        <text
          x={cx}
          y={cy}
          dy={8}
          textAnchor="middle"
          fill={fill}
          className="text-sm"
        >
          {payload.name}
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
          fillOpacity={fillOpacity}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={fill}
        />
        <path
          d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
          stroke={fill}
          fill="none"
        />
        <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          textAnchor={textAnchor}
          fill="#6b7280"
          className="text-sm"
        >
          {payload.name}
        </text>
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          dy={18}
          textAnchor={textAnchor}
          fill="#999"
          className="text-sm"
        >
          {`${(percent * 100).toFixed(2)}%`}
        </text>
      </g>
    );
  };

  const handlePieEnter = (_, index) => setActiveIndex(index);

  return (
    <div className="mt-10">
      <p className="text-gray-400 ml-10">Registration Fee</p>
      <div className="flex items-center">
        {isLoading && (
          <div className="flex justify-center items-center space-x-1 h-64">
            <div className="bg-blue-400 w-2 h-2 full rounded-full animate-bounce blue-circle"></div>
            <div className="bg-blue-400 w-2 h-2 full rounded-full animate-bounce blue-circle"></div>
            <div className="bg-blue-400 w-2 h-2 full rounded-full animate-bounce blue-circle"></div>
          </div>
        )}
        {!isLoading && (
          <ResponsiveContainer width="100%" height={250} className="mt-0">
            <PieChart
              width={100}
              height={100}
              margin={{ top: 10, left: 10, right: 10, bottom: 20 }}
            >
              <Pie
                activeIndex={activeIndex}
                activeShape={renderActiveShape}
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={75}
                dataKey="total_count"
                fillOpacity={0.6}
                onMouseEnter={handlePieEnter}
              >
                <Cell fill="#e4e6eb" />
                <Cell fill="#60a5fa" />
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        )}
        <div className="mr-20">
          <Link
            to={`/tax-indexes?type=3&status=2&year=${year}`}
            className="flex cursor-pointer"
          >
            <p className="text-blue-500">{filedCount}</p>
            <span className="ml-2 text-gray-400 ">Filed</span>
          </Link>
          <Link
            to={`/tax-indexes?type=3&status=1&year=${year}`}
            className="flex cursor-pointer"
          >
            <p className="text-blue-500">{unfiledCount}</p>
            <span className="ml-2 text-gray-400">Unfiled</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegistrationFeeSubmission;
